import React, { useEffect, useState } from 'react';
import { CoursePipelineSidebar } from './CoursePipelineSidebar';
import { LessonPipelineSidebar } from './LessonPipelineSidebar';
import { CourseContextProvider, useCourseContext } from '../../CourseContext';
import { LessonContextProvider, useLessonContext } from '../../LessonContext';
import { CourseSetup } from '../../stages/course/CourseSetup';
import { CourseSyllabus } from '../../stages/course/CourseSyllabus';
import { CourseSyllabusReview } from '../../stages/course/CourseSyllabusReview';
import { CourseLessonsDashboard } from '../../stages/course/CourseLessonsDashboard';
import { CourseArtwork } from '../../stages/course/CourseArtwork';
import { CoursePublish } from '../../stages/course/CoursePublish';
import { LessonSetup } from '../../stages/lesson/LessonSetup';
import { LessonGeneration } from '../../stages/lesson/LessonGeneration';
import { LessonReview } from '../../stages/lesson/LessonReview';
import { LessonExerciseGen } from '../../stages/lesson/LessonExerciseGen';
import { LessonExerciseReview } from '../../stages/lesson/LessonExerciseReview';
import { LessonImageGen } from '../../stages/lesson/LessonImageGen';
import { LessonAudioGen } from '../../stages/lesson/LessonAudioGen';
import { LessonPreview } from '../../stages/lesson/LessonPreview';
import { LessonCompile } from '../../stages/lesson/LessonCompile';
import { CourseDocumentEditor } from '../../CourseDocumentEditor';
import { courseCandidatesService, CourseCandidate } from '../../../../services/courseCandidatesService';
import { courseRunsService, CourseRun, LessonRun } from '../../../../services/courseRunsService';

/** The full classic course-building experience — course pipeline sidebar,
    stage panels, lessons dashboard, and per-lesson pipelines — as one
    embeddable component. Used by Course Generation (course selected from the
    list) and by the Course Design render page (Talaera target). */
export function CourseWorkspace({ courseId, runId, backLabel, onBack, onError }: {
  courseId: string;
  /** Pin to a specific course run. Without it: first run, created if none. */
  runId?: string;
  backLabel?: string;
  onBack?: () => void;
  onError?: (message: string) => void;
}) {
  const [candidate, setCandidate] = useState<CourseCandidate | null>(null);
  const [run, setRun] = useState<CourseRun | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const cand = await courseCandidatesService.get(courseId);
        if (cancelled) return;
        setCandidate(cand);

        if (runId) {
          const pinned = await courseRunsService.get(runId);
          if (!cancelled) setRun(pinned);
          return;
        }
        const { runs } = await courseRunsService.list(courseId);
        if (cancelled) return;
        if (runs.length > 0) {
          setRun(runs[0]);
        } else {
          const newRun = await courseRunsService.create(courseId);
          if (!cancelled) setRun(newRun);
        }
      } catch (e: unknown) {
        if (!cancelled) onError?.(e instanceof Error ? e.message : 'Failed to load course');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void load();
    return () => { cancelled = true; };
    // onError: callers pass a stable setState function
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseId, runId]);

  if (loading || !candidate || !run) {
    return (
      <div className="flex flex-col items-center justify-center h-32">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-talaera-blue"></div>
        <p className="mt-4 text-gray-600">Loading course...</p>
      </div>
    );
  }

  return (
    <CourseContextProvider candidate={candidate} run={run}>
      <CourseDetailView candidate={candidate} backLabel={backLabel} onBack={onBack} />
    </CourseContextProvider>
  );
}

function CourseDetailView({
  candidate,
  backLabel,
  onBack,
}: {
  candidate: CourseCandidate;
  backLabel?: string;
  onBack?: () => void;
}) {
  const { activeLessonRunId, setActiveLessonRunId } = useCourseContext();

  if (activeLessonRunId) {
    return (
      <LessonPipelineView
        lessonRunId={activeLessonRunId}
        courseTitle={candidate.title}
        onBack={() => setActiveLessonRunId(null)}
      />
    );
  }

  return (
    <CoursePipelineView candidate={candidate} backLabel={backLabel} onBack={onBack} />
  );
}

function CoursePipelineView({
  candidate,
  backLabel,
  onBack,
}: {
  candidate: CourseCandidate;
  backLabel?: string;
  onBack?: () => void;
}) {
  const [viewMode, setViewMode] = useState<'stages' | 'document'>('stages');

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
      {/* Header row */}
      <div className="lg:col-span-12">
        <div className="flex items-center mb-4">
          {onBack ? (
            <button
              onClick={onBack}
              className="text-sm text-gray-500 hover:text-gray-700 shrink-0"
            >
              &larr; {backLabel ?? 'Back'}
            </button>
          ) : (
            <div className="shrink-0" />
          )}
          <h2 className="text-lg font-semibold text-gray-900 text-center flex-1">
            {candidate.title}
          </h2>
          <div className="flex items-center gap-3 shrink-0">
            <div className="flex items-center bg-gray-100 rounded-md p-0.5 text-xs">
              <button
                type="button"
                className={`px-3 py-1 rounded transition-all ${viewMode === 'document' ? 'bg-white shadow-sm text-gray-900 font-medium' : 'text-gray-500 hover:text-gray-700'}`}
                onClick={() => setViewMode('document')}
              >
                Document
              </button>
              <button
                type="button"
                className={`px-3 py-1 rounded transition-all ${viewMode === 'stages' ? 'bg-white shadow-sm text-gray-900 font-medium' : 'text-gray-500 hover:text-gray-700'}`}
                onClick={() => setViewMode('stages')}
              >
                Stages
              </button>
            </div>
          </div>
        </div>
      </div>

      {viewMode === 'document' ? (
        <div className="lg:col-span-12">
          <div className="bg-white border rounded p-4">
            <CourseDocumentEditor />
          </div>
        </div>
      ) : (
        <>
          <div className="lg:col-span-3">
            <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Pipeline</h3>
            <div className="relative">
              <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-gray-200" />
              <CoursePipelineSidebar />
            </div>
          </div>
          <div className="lg:col-span-9">
            <div className="bg-white border rounded p-4">
              <CourseStageContent />
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function LessonPipelineView({
  lessonRunId,
  courseTitle,
  onBack,
}: {
  lessonRunId: string;
  courseTitle: string;
  onBack: () => void;
}) {
  const [lessonRun, setLessonRun] = useState<LessonRun | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const lr = await courseRunsService.getLessonRun(lessonRunId);
        if (!cancelled) setLessonRun(lr);
      } catch (e) {
        console.error('Failed to load lesson run:', e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [lessonRunId]);

  if (loading || !lessonRun) {
    return (
      <div className="flex flex-col items-center justify-center h-32">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-talaera-blue"></div>
        <p className="mt-3 text-sm text-gray-500">Loading lesson...</p>
      </div>
    );
  }

  return (
    <LessonContextProvider lessonRun={lessonRun}>
      <LessonDetailContent lessonRun={lessonRun} courseTitle={courseTitle} onBack={onBack} />
    </LessonContextProvider>
  );
}

function LessonDetailContent({
  lessonRun,
  courseTitle,
  onBack,
}: {
  lessonRun: LessonRun;
  courseTitle: string;
  onBack: () => void;
}) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
      {/* Header */}
      <div className="lg:col-span-12">
        <div className="flex items-center mb-4">
          <button onClick={onBack} className="text-sm text-gray-500 hover:text-gray-700 shrink-0">
            &larr; Back to {courseTitle}
          </button>
          <h2 className="text-lg font-semibold text-gray-900 text-center flex-1">
            Lesson {lessonRun.lesson_number}: {lessonRun.title}
          </h2>
          <div className="shrink-0" />
        </div>
      </div>

      {/* Lesson pipeline sidebar */}
      <div className="lg:col-span-3">
        <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Lesson Pipeline</h3>
        <div className="relative">
          <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-gray-200" />
          <LessonPipelineSidebar />
        </div>
      </div>

      {/* Lesson stage content */}
      <div className="lg:col-span-9">
        <div className="bg-white border rounded p-4">
          <LessonStageContent />
        </div>
      </div>
    </div>
  );
}

function LessonStageContent() {
  const { selectedStageId } = useLessonContext();

  switch (selectedStageId) {
    case 'lesson_setup':
      return <LessonSetup />;
    case 'lesson_generation':
      return <LessonGeneration />;
    case 'lesson_review':
      return <LessonReview />;
    case 'lesson_exercise_gen':
      return <LessonExerciseGen />;
    case 'lesson_exercise_review':
      return <LessonExerciseReview />;
    case 'lesson_image_gen':
      return <LessonImageGen />;
    case 'lesson_audio_gen':
      return <LessonAudioGen />;
    case 'lesson_assembly':
    case 'lesson_preview':
      return <LessonPreview />;
    case 'lesson_compile':
      return <LessonCompile />;
    default:
      return <div className="text-sm text-gray-500">Select a stage to view details.</div>;
  }
}

function CourseStageContent() {
  const { selectedStageId } = useCourseContext();

  switch (selectedStageId) {
    case 'course_setup':
      return <CourseSetup />;
    case 'course_syllabus':
      return <CourseSyllabus />;
    case 'course_syllabus_review':
      return <CourseSyllabusReview />;
    case 'course_lessons':
      return <CourseLessonsDashboard />;
    case 'course_artwork':
      return <CourseArtwork />;
    case 'course_publish':
      return <CoursePublish />;
    default:
      return <div className="text-sm text-gray-500">Select a stage to view details.</div>;
  }
}
